/**
 * DataDog Logging Utilities
 *
 * Functions for logging trace completion and errors to DataDog
 * with encrypted test code for later reproduction.
 */

import { datadogLogs } from "@datadog/browser-logs";
import { sha256 } from "js-sha256"; // Lightweight library
import { encryptTestCode } from "./encryption";
import type { GeneratedTest } from "./types";

const SECRET_SALT =
  process.env.NEXT_PUBLIC_DATADOG_NAME_SALT || "e030eaa3-6e79-41b0-89cd"; // Keep this in env vars

function getConsumerKey(name: string): string {
  if (!name || name === "Unknown") return "Unknown";

  // Normalize the name to ensure "John Doe" and "john doe " produce the same key
  const normalizedName = name.toLowerCase().trim();

  // Create a keyed hash and take a slice for readability
  return sha256.hmac(SECRET_SALT, normalizedName).substring(0, 12);
}

// ============================================================================
// Configuration
// ============================================================================

// Mixpanel project/view IDs - different per environment, set in .env files
// Find yours at: mixpanel.com/project/{PROJECT_ID}/view/{VIEW_ID}/app/events
const MIXPANEL_PROJECT_ID = process.env.NEXT_PUBLIC_MIXPANEL_PROJECT_ID || "";
const MIXPANEL_VIEW_ID = process.env.NEXT_PUBLIC_MIXPANEL_VIEW_ID || "";

// ============================================================================
// Mixpanel URL Builder
// ============================================================================

/**
 * Build a Mixpanel events URL filtered by kiosk session ID
 */
function buildMixpanelSessionUrl(sessionId: string): string | null {
  if (!MIXPANEL_PROJECT_ID || !MIXPANEL_VIEW_ID) return null;

  const filter = encodeURIComponent(
    JSON.stringify([
      {
        propertyName: "kiosk_session_id",
        propertyObjectType: "event",
        propertyType: "string",
        filterOperator: "equals",
        filterValue: sessionId,
      },
    ]),
  );

  return `https://mixpanel.com/project/${MIXPANEL_PROJECT_ID}/view/${MIXPANEL_VIEW_ID}/app/events?filters=${filter}`;
}

// ============================================================================
// Logging Functions
// ============================================================================

/**
 * Log trace completion to DataDog with full test code
 * Called from kiosk-renderer when entering completion states
 */
export function logTraceCompletionToDataDog(
  test: GeneratedTest,
  triggerEvent: string,
): void {
  const mixpanelUrl = buildMixpanelSessionUrl(test.metadata.traceId);

  const consumerKey = getConsumerKey(test.metadata.consumerName || "Unknown");
  datadogLogs.logger.info(
    `${consumerKey} completed kiosk - ${test.metadata.traceId}`,
    {
      traceId: test.metadata.traceId,
      consumerKey,
      triggerEvent,
      initialState: test.metadata.initialState,
      finalState: test.metadata.finalState,
      eventCount: test.metadata.eventCount,
      domEventCount: test.metadata.domEventCount,
      duration: test.metadata.duration,
      generatedAt: test.metadata.generatedAt,
      testFilename: `trace-${test.metadata.traceId}.spec.ts`,
      testCode: encryptTestCode(test.testCode),
      ...(mixpanelUrl && { mixpanelUrl }),
      s3FixtureId: test.metadata.fixtureId,
    },
  );

  console.log(
    `[playwright-generator] Logged trace ${test.metadata.traceId} to DataDog`,
  );
  if (mixpanelUrl) {
    console.log(`[playwright-generator] Mixpanel session: ${mixpanelUrl}`);
  }
}

/**
 * Error codes that represent expected business outcomes (card declines,
 * user cancellations, reader timeouts) rather than engineering problems.
 * These are logged at `warn` level so they don't page on-call.
 */
const EXPECTED_POS_PAYMENT_ERROR_CODES = new Set([
  "PAYMENT_FAILED",
  "PAYMENT_CANCELED",
  "TIMEOUT",
]);

/**
 * Log trace on error to DataDog with full test code and error context
 * Called from ErrorDisplay when an error screen is shown
 * This allows creating reproduction tests for bugs
 */
export function logTraceErrorToDataDog(
  test: GeneratedTest,
  errorContext?: {
    code: string;
    message: string;
    retryable: boolean;
    stack?: string;
  },
): void {
  const mixpanelUrl = buildMixpanelSessionUrl(test.metadata.traceId);
  const consumerKey = getConsumerKey(test.metadata.consumerName || "Unknown");

  const message = `${consumerKey} hit kiosk error - ${test.metadata.traceId}`;
  const metadata = {
    error: {
      kind: test.metadata.finalState,
      message: errorContext?.message || "Unknown kiosk error",
      stack:
        errorContext?.stack ||
        new Error(errorContext?.message || "Unknown kiosk error").stack,
    },
    traceId: test.metadata.traceId,
    consumerKey,
    errorCode: errorContext?.code,
    errorMessage: errorContext?.message,
    errorRetryable: errorContext?.retryable,
    initialState: test.metadata.initialState,
    finalState: test.metadata.finalState,
    eventCount: test.metadata.eventCount,
    domEventCount: test.metadata.domEventCount,
    duration: test.metadata.duration,
    generatedAt: test.metadata.generatedAt,
    testFilename: `error-trace-${test.metadata.traceId}.spec.ts`,
    testCode: test.testCode,
    ...(mixpanelUrl && { mixpanelUrl }),
    s3FixtureId: test.metadata.fixtureId,
  };

  if (
    errorContext?.code &&
    EXPECTED_POS_PAYMENT_ERROR_CODES.has(errorContext.code)
  ) {
    datadogLogs.logger.warn(message, metadata);
  } else {
    datadogLogs.logger.error(message, metadata);
  }

  console.log(
    `[playwright-generator] Logged error trace ${test.metadata.traceId} to DataDog`,
  );
}
